import { EventApi, EventInput } from '@fullcalendar/core';
import { isSameDay } from '@siat-fe/core';
import { Item } from '../form/select-field/select-field.component';
import { CALENDAR_EVENT_STATE, MacchinarioDto } from './models/calendar.model';

export { isSameDay };

export const EVENT_MIN_VISUAL_MINUTES = 20;
export const CLICK_SNAP_MINUTES = 15;

/**
 * Builds the same "day of week / day of month" header markup used by the
 * standard calendar's `dayHeaderContent` (week/month views) — shared here so
 * the resource-timeline view's `slotLabelContent` renders identical day
 * headers instead of a second, separately-maintained implementation.
 */
export function renderDayHeaderHtml(
  date: Date,
  translateLang: string,
  isHoliday: boolean
): { html: string } {
  const isToday = isSameDay(date, new Date());
  const dayOfWeek = date.toLocaleDateString(translateLang, {
    weekday: 'short',
  });
  const dayOfMonth = date.getDate();

  return {
    html: `
      <div class="day-header ${isHoliday ? 'holiday-day' : ''}">
        <div class="day-of-week">${dayOfWeek}</div>
        <div class="day-of-month ${isToday ? 'today' : ''}">${dayOfMonth}</div>
      </div>
    `,
  };
}

/**
 * True if a start/end pair falls on different calendar days — used to decide
 * whether a dialog header must also show the end date, since an end time
 * like "00:00" alone would be ambiguous about which day it refers to.
 */
export function isMultiDayEvent(
  start: Date | string | null | undefined,
  end: Date | string | null | undefined
): boolean {
  if (!start || !end) return false;
  return !isSameDay(new Date(start), new Date(end));
}

/**
 *
 * @return {number} minutes between `start` and `end`, or `null` if either is `null`.
 */
export function computeDurationMinutes(
  start: Date | string | null | undefined,
  end: Date | string | null | undefined
): number | null {
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;
  const minutes = Math.round((endDate.getTime() - startDate.getTime()) / 60000);
  return minutes >= 0 ? minutes : null;
}

/**
 * @return {string} duration label (e.g. "30min", "1h 30min", "2h")
 */
export function formatDurationLabel(minutes: number | null): string {
  if (minutes == null) return '-';
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0
    ? `${hours}h ${remainingMinutes}min`
    : `${hours}h`;
}

/**
 * Convert a UTC time string to a local time string.
 */
export function convertUtcTimeToLocal(utcTimeString: string): string {
  const todayStr = new Date().toISOString().split('T')[0];
  const utcDate = new Date(`${todayStr}T${utcTimeString}Z`);
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(utcDate);
}

/**
 * Opening window of a structure, as local 'HH:mm' times and FullCalendar slot bounds.
 */
export interface FasciaOraria {
  inizio: string;
  fine: string;
  slotMinTime: string;
  slotMaxTime: string;
}

/**
 * Reads a structure's opening hours (local time, a trailing 'Z' is ignored) into a window.
 * @returns {FasciaOraria | null} The window, null when a time is missing/invalid or the end is not after the start
 */
export function getFasciaOraria(
  oraInizio?: string | null,
  oraFine?: string | null
): FasciaOraria | null {
  const inizio = toOraHHmm(oraInizio);
  const fine = toOraHHmm(oraFine);
  if (!inizio || !fine || fine <= inizio) return null;
  return {
    inizio,
    fine,
    slotMinTime: `${inizio}:00`,
    slotMaxTime: `${fine}:00`,
  };
}

/**
 * Widens a window to the whole hours needed to show the given events, e.g. travel times before opening.
 * @returns {FasciaOraria} The window itself when every event already fits in it
 */
export function estendiFasciaOraria(
  fascia: FasciaOraria,
  eventi: EventInput[]
): FasciaOraria {
  let inizio = fascia.inizio;
  let fine = fascia.fine;
  for (const evento of eventi) {
    if (!evento.start || !evento.end || evento.allDay) continue;
    const start = new Date(evento.start as Date | string | number);
    const end = new Date(evento.end as Date | string | number);
    const oraInizio = `${String(start.getHours()).padStart(2, '0')}:00`;
    const oraFine = isSameCalendarDay(start, end)
      ? `${String(end.getMinutes() > 0 ? end.getHours() + 1 : end.getHours()).padStart(2, '0')}:00`
      : '24:00';
    if (oraInizio < inizio) inizio = oraInizio;
    if (oraFine > fine) fine = oraFine;
  }
  if (inizio === fascia.inizio && fine === fascia.fine) return fascia;
  return {
    inizio,
    fine,
    slotMinTime: `${inizio}:00`,
    slotMaxTime: `${fine}:00`,
  };
}

/**
 * Tells whether two dates fall on the same calendar day.
 * @returns {boolean} True when year, month and day match
 */
function isSameCalendarDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

/**
 * Tells whether a dragged or resized calendar event stays within the opening window of its start day.
 * @returns {boolean} True without a window, for external drops (no dragged event) and for all-day spans
 */
export function isSpostamentoEntroFasciaOraria(
  span: { start: Date; end: Date; allDay: boolean },
  draggedEvent: unknown,
  fascia: Pick<FasciaOraria, 'inizio' | 'fine'> | null
): boolean {
  if (!fascia || !draggedEvent || span.allDay) return true;
  return (
    span.start >= withOra(span.start, fascia.inizio) &&
    span.end <= withOra(span.start, fascia.fine)
  );
}

/**
 * Returns the given day at the given 'HH:mm' time.
 * @returns {Date} A new Date on `day` at `ora`
 */
export function withOra(day: Date, ora: string): Date {
  const [ore, minuti] = ora.split(':').map(Number);
  const result = new Date(day);
  result.setHours(ore, minuti, 0, 0);
  return result;
}

/**
 * Extracts the leading 'HH:mm' of a time string such as '08:00:00Z'.
 * @returns {string | null} The 'HH:mm' time, null when the string does not start with a valid time
 */
function toOraHHmm(value?: string | null): string | null {
  const match = value?.match(/^([01]\d|2[0-3]):([0-5]\d)/);
  return match ? `${match[1]}:${match[2]}` : null;
}

/**
 * Convert an array of macchinari to an array of items.
 */
export function mapMacchinariToItems(
  macchinari: MacchinarioDto[] | undefined
): Item[] {
  if (!macchinari?.length) return [];
  return macchinari.map(m => ({
    id: m.id,
    name: m.nome ?? '',
    durataSlotMacchinario: m.durataSlotMacchinario,
    orariAttivita: m.orariAttivita,
  }));
}

/**
 * Snap the date to the nearest slot of the active machinery.
 */
export function snapDateToMacchinarioSlot(
  date: Date,
  macchinario: Item | null | undefined
): Date | null {
  const durata = macchinario?.durataSlotMacchinario;
  const orari = macchinario?.orariAttivita;
  if (!durata || durata <= 0 || !orari?.length) return null;

  const minutiDelGiorno = date.getHours() * 60 + date.getMinutes();
  for (const o of orari) {
    const [inizioH, inizioM] = convertUtcTimeToLocal(o.ora_inizio)
      .split(':')
      .map(Number);
    const [fineH, fineM] = convertUtcTimeToLocal(o.ora_fine)
      .split(':')
      .map(Number);
    const inizioMinuti = inizioH * 60 + inizioM;
    const fineMinuti = fineH * 60 + fineM;
    if (minutiDelGiorno < inizioMinuti || minutiDelGiorno >= fineMinuti) {
      continue;
    }
    const numeroSlot = Math.round((minutiDelGiorno - inizioMinuti) / durata);
    const minutiSlotMassimi = fineMinuti - inizioMinuti - durata;
    const offsetMinuti = Math.max(
      0,
      Math.min(numeroSlot * durata, minutiSlotMassimi)
    );
    const snappedMinuti = inizioMinuti + offsetMinuti;
    const snapped = new Date(date);
    snapped.setHours(Math.floor(snappedMinuti / 60), snappedMinuti % 60, 0, 0);
    return snapped;
  }
  return null;
}

/**
 * True if there is a machinery booking conflict.
 */
export function hasMacchinarioBookingConflict(
  idMacchinario: unknown,
  start: Date,
  end: Date,
  events: EventInput[],
  excludeEventId?: string | number
): boolean {
  if (!hasValidMachineryId(idMacchinario)) return false;
  return events.some(e => {
    if (excludeEventId != null && String(e.id) === String(excludeEventId)) {
      return false;
    }
    const eventMacchinario = e.extendedProps?.['idMacchinario'];
    if (
      !hasValidMachineryId(eventMacchinario) ||
      String(eventMacchinario) !== String(idMacchinario)
    ) {
      return false;
    }
    const eStart = new Date(e.start as string).getTime();
    const eEnd = new Date((e.end ?? e.start) as string).getTime();
    return start.getTime() < eEnd && eStart < end.getTime();
  });
}

/**
 * True if the machinery id is valid.
 * @return {boolean}
 */
export function hasValidMachineryId(id: unknown): boolean {
  if (id == null) return false;
  const normalized = String(id).trim().toLowerCase();
  return normalized !== '' && normalized !== '0' && normalized !== 'null';
}

/**
 * End date for the initial 3-day range view, two days after start.
 */
export function calculateEndDate(startDate: Date): Date {
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 2);
  return endDate;
}

/**
 * Current time formatted as 'HH:mm:00', used for FullCalendar's 'scrollTime'.
 */
export function getScrollTimeString(): string {
  const now = new Date();
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}:00`;
}

/**
 * True if the event is already in the past.
 */
export function isEventInPast(event: EventApi): boolean {
  if (!event || !event.end) {
    return false;
  }
  return event.end < new Date();
}

/**
 * Label for FullCalendar's "+N" more-events link, localized to Italian.
 */
export function getMoreLinkLabel(lang: string | undefined): string {
  return lang?.startsWith('it') ? 'altri' : 'more';
}

/**
 * Rounds a date's minutes down to the nearest quarter hour (:00/:15/:30/:45)
 * — the drop-target granularity for rescheduling via drag & drop, matching
 * `snapDuration` in calendar-options.builder.ts.
 */
export function roundDownToQuarterHour(date: Date): Date {
  const rounded = new Date(date);
  const roundedMinutes = Math.floor(rounded.getMinutes() / 15) * 15;
  rounded.setMinutes(roundedMinutes, 0, 0);
  return rounded;
}

/**
 * Date formatted as 'YYYY-MM-DD', used as a lookup key into the holiday set.
 */
export function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/*
 * True if two events' time ranges overlap.
 */
function eventsOverlap(a: EventInput, b: EventInput): boolean {
  const aStart = new Date(a.start as string).getTime();
  const aEnd = new Date(a.end as string).getTime();
  const bStart = new Date(b.start as string).getTime();
  const bEnd = new Date(b.end as string).getTime();
  return aStart < bEnd && bStart < aEnd;
}

/**
 * The maximum number of events to show per cluster (connected component of
 */
const MAX_VISIBLE_PER_CLUSTER = 3;

/**
 * Clusters overlapping events into groups of up to `MAX_VISIBLE_PER_CLUSTER`
 * @return {EventInput[]} visible cluster members plus one overflow event per oversized cluster.
 */
export function groupOverlappingEvents(events: EventInput[]): EventInput[] {
  const allDayEvents = events.filter(e => e.allDay);
  const timedEvents = events.filter(e => !e.allDay);

  const sorted = [...timedEvents].sort(
    (a, b) =>
      new Date(a.start as string).getTime() -
      new Date(b.start as string).getTime()
  );
  const consumed = new Set<EventInput>();
  const result: EventInput[] = [...allDayEvents];

  sorted.forEach(event => {
    if (consumed.has(event)) return;

    const cluster = [event];
    consumed.add(event);
    let grew = true;
    while (grew) {
      grew = false;
      sorted.forEach(candidate => {
        if (consumed.has(candidate)) return;
        if (cluster.some(member => eventsOverlap(member, candidate))) {
          cluster.push(candidate);
          consumed.add(candidate);
          grew = true;
        }
      });
    }

    const laneCount = Math.min(cluster.length, MAX_VISIBLE_PER_CLUSTER);
    const visible = cluster.slice(0, MAX_VISIBLE_PER_CLUSTER);
    const hidden = cluster.slice(MAX_VISIBLE_PER_CLUSTER);

    visible.forEach((member, laneIndex) => {
      result.push({
        ...member,
        extendedProps: { ...member.extendedProps, laneIndex, laneCount },
      });
    });

    if (hidden.length === 0) return;

    result.push({
      id: `${visible[0].id}-cluster-overflow`,
      title: `+ ${hidden.length} altri`,
      start: visible[0].start,
      end: visible[0].end,
      allDay: false,
      display: 'block',
      editable: false,
      startEditable: false,
      durationEditable: false,
      extendedProps: {
        isClusterOverflow: true,
        extraEvents: hidden,
        extraEventsCount: hidden.length,
        laneIndex: laneCount,
        laneCount: laneCount + 1,
      },
    });
  });

  return result;
}

/**
 * True if a past "appuntamento" has no outcome recorded at all — no stato
 * esito, no erogazione registrata, no avviso paziente — meaning nobody has
 * followed up on it yet. Used to flag it for attention; deliberately scoped
 * to `appuntamento-event` only (not promemoria/turno/assenza/riunione).
 */
export function isAppuntamentoUnresolved(event: {
  end?: unknown;
  classNames?: unknown;
  extendedProps?: {
    stato?: string;
    dataInizioErogazione?: Date | string | null;
    dataFineErogazione?: Date | string | null;
    avvisoAssenzaPaziente?: boolean | null;
  };
}): boolean {
  const classNames = Array.isArray(event.classNames) ? event.classNames : [];
  if (!classNames.includes('appuntamento-event')) return false;
  if (!event.end) return false;
  const end = new Date(event.end as string | number | Date);
  if (isNaN(end.getTime()) || end >= new Date()) return false;

  const props = event.extendedProps ?? {};
  const hasStatoEsito =
    props.stato === CALENDAR_EVENT_STATE.NON_SVOLTO ||
    props.stato === CALENDAR_EVENT_STATE.NON_SVOLTO_E_INVIATO_A_CASSA ||
    props.stato === CALENDAR_EVENT_STATE.SVOLTO_E_INVIATO_A_CASSA;
  const hasErogazioneRegistrata =
    !!props.dataInizioErogazione && !!props.dataFineErogazione;
  const hasAvvisoRegistrato = props.avvisoAssenzaPaziente != null;

  return !hasStatoEsito && !hasErogazioneRegistrata && !hasAvvisoRegistrato;
}

/**
 * True if the event already has an outcome registered — a `stato` or an
 * erogazione start date. Used to hide quick actions (e.g. "Pianificazione",
 * "Erogazione") once there's nothing left to plan or register.
 */
export function hasEsitoRegistrato(event: {
  extendedProps?: {
    stato?: string | null;
    dataInizioErogazione?: Date | string | null;
  };
}): boolean {
  const props = event.extendedProps ?? {};
  return !!(props.stato || props.dataInizioErogazione);
}

/**
 * True if `event` is a "promemoria": used to force a compact fixed height
 * instead of one proportional to its (often long, low-signal) duration.
 */
export function isPromemoriaEvent(event: {
  extendedProps?: { tipoEvento?: { nome?: string } };
}): boolean {
  return event?.extendedProps?.tipoEvento?.nome?.toLowerCase() === 'promemoria';
}

/**
 * True if `event` is a "riunione"
 */
export function isRiunioneEvent(event: {
  extendedProps?: { tipoEvento?: { id?: number; nome?: string } };
}): boolean {
  const tipoEvento = event?.extendedProps?.tipoEvento;
  return tipoEvento?.nome?.toLowerCase() === 'riunione';
}

/**
 * True if `event` is an "assenza"
 */
export function isAssenzaEvent(event: {
  extendedProps?: { tipoEvento?: { id?: number; nome?: string } };
}): boolean {
  const tipoEvento = event?.extendedProps?.tipoEvento;
  return tipoEvento?.id === 3 || tipoEvento?.nome?.toLowerCase() === 'assenza';
}

/**
 * True if `event` is a "turno"
 */
export function isTurnoEvent(event: {
  extendedProps?: { tipoEvento?: { id?: number; nome?: string } };
}): boolean {
  const tipoEvento = event?.extendedProps?.tipoEvento;
  return tipoEvento?.id === 4 || tipoEvento?.nome?.toLowerCase() === 'turno';
}

/**
 * True if `event` is a "travel time"
 */
export function isTravelEvent(event: {
  extendedProps?: { tipoEvento?: { id?: number; nome?: string } };
}): boolean {
  const tipoEvento = event?.extendedProps?.tipoEvento;
  return (
    tipoEvento?.id === 2 || tipoEvento?.nome?.toLowerCase() === 'travel time'
  );
}

/**
 * To month view events, remove allDay events with more than one occurrence
 * and the draggable header copies of the assenze
 */
export function toMonthViewEvents(events: EventInput[]): EventInput[] {
  const occurrences = new Map<string, number>();
  events.forEach(e => {
    const key = String(e.id);
    occurrences.set(key, (occurrences.get(key) ?? 0) + 1);
  });

  return events
    .filter(e => !e.extendedProps?.['idEventoReale'])
    .filter(e => !(e.allDay && (occurrences.get(String(e.id)) ?? 0) > 1))
    .map(e =>
      e.display === 'background' && e.groupId !== 'disponibilita-macchinario'
        ? { ...e, display: 'block' }
        : e
    );
}

/**
 * @returns {number}
 * The z-index of an event is 1 for assenze and 2 for everything else
 */
export function getEventZIndex(isAssenza: boolean): number {
  return isAssenza ? 1 : 2;
}

const fmtTime = (d: Date) =>
  `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
const fmtDate = (d: Date) =>
  `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;

/**
 * @return {string} "HH:mm - HH:mm" or "da HH:mm del DD/MM a HH:mm del DD/MM"
 */
export function formatDateAwareTimeRange(start: Date, end: Date): string {
  return isSameDay(start, end)
    ? `${fmtTime(start)} - ${fmtTime(end)}`
    : `da ${fmtTime(start)} del ${fmtDate(start)} a ${fmtTime(end)} del ${fmtDate(end)}`;
}

/**
 * Format an all-day date range as "dalle HH:mm del DD/MM alle HH:mm del DD/MM"
 * @returns {string}
 */
export function formatAllDayDateRange(start: Date, end: Date): string {
  return `dalle ${fmtTime(start)} del ${fmtDate(start)} alle ${fmtTime(end)} del ${fmtDate(end)}`;
}

/**
 * Format a turno's start and end time.
 * @return {string}
 */
export function formatTurnoTimeRange(start: Date, end: Date): string {
  return isSameDay(start, end)
    ? `${fmtTime(start)} - ${fmtTime(end)}`
    : formatAllDayDateRange(start, end);
}

/**
 * Format an event's start time as "HH:mm"
 * @return {string}
 */
export function formatTime(date: Date): string {
  return fmtTime(date);
}

/**
 * Format a promemoria's start and end time.
 * @return {string}
 */
export function formatPromemoriaAllDayDeadline(start: Date, end: Date): string {
  return isSameDay(start, end)
    ? `scade (alle ${fmtTime(end)})`
    : `scade (il ${fmtDate(end)} alle ${fmtTime(end)})`;
}

/**
 * Format a promemoria's start and end time.
 * @return {string}
 */
export function formatPromemoriaDeadline(start: Date, end: Date): string {
  const fmtTime = (d: Date) =>
    `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  if (isSameDay(start, end)) return fmtTime(end);
  const nextDay = new Date(start);
  nextDay.setDate(nextDay.getDate() + 1);
  if (isSameDay(nextDay, end)) return `Domani ${fmtTime(end)}`;
  return `${end.getDate().toString().padStart(2, '0')}/${(end.getMonth() + 1).toString().padStart(2, '0')} ${fmtTime(end)}`;
}
